import { useId, type ReactNode } from 'react'
import { cx } from '../lib/cx'
import { Icon, type IconName } from './Icon'
import './ChoiceGroup.css'

export interface ChoiceOption<T extends string> {
  value: T
  title: string
  description?: ReactNode
  icon?: IconName
  disabled?: boolean
  /** Why it cannot be chosen, shown under it and read out with it. */
  disabledReason?: string
}

export interface ChoiceGroupProps<T extends string> {
  legend: string
  hideLegend?: boolean
  name: string
  value: T
  onChange: (value: T) => void
  options: readonly ChoiceOption<T>[]
  /** Two columns when each card can be at least 15rem wide. */
  columns?: 1 | 2
  className?: string
}

/**
 * Radio buttons drawn as cards: one choice, with a title, a sentence of explanation
 * and, for options that are not available, the reason why. Native radios, so arrow
 * keys and screen readers behave as expected.
 */
export function ChoiceGroup<T extends string>({
  legend,
  hideLegend = false,
  name,
  value,
  onChange,
  options,
  columns = 1,
  className,
}: ChoiceGroupProps<T>) {
  const groupId = useId()
  return (
    <fieldset className={cx('choice-group', columns === 2 && 'choice-group--2', className)}>
      <legend className={cx('choice-group__legend', hideLegend && 'visually-hidden')}>{legend}</legend>
      <div className="choice-group__options">
        {options.map((option) => {
          const id = `${groupId}-${option.value}`
          const describedBy = [option.description ? `${id}-description` : '', option.disabled && option.disabledReason ? `${id}-reason` : '']
            .filter(Boolean)
            .join(' ')
          const checked = option.value === value
          return (
            <label
              key={option.value}
              htmlFor={id}
              className={cx('choice', checked && 'choice--checked', option.disabled && 'choice--disabled')}
            >
              <input
                id={id}
                className="choice__input"
                type="radio"
                name={name}
                value={option.value}
                checked={checked}
                disabled={option.disabled}
                aria-describedby={describedBy || undefined}
                onChange={() => onChange(option.value)}
              />
              <span className="choice__mark" aria-hidden="true" />
              <span className="choice__text">
                <span className="choice__title">
                  {option.icon && <Icon name={option.icon} size={20} />}
                  {option.title}
                </span>
                {option.description && (
                  <span id={`${id}-description`} className="choice__description">
                    {option.description}
                  </span>
                )}
                {option.disabled && option.disabledReason && (
                  <span id={`${id}-reason`} className="choice__reason">
                    <Icon name="info" size={20} />
                    <span>{option.disabledReason}</span>
                  </span>
                )}
              </span>
            </label>
          )
        })}
      </div>
    </fieldset>
  )
}
